import { GetCollegePredictorRequestType } from '@nw/shared/data-access';

import {
  jeeMainsAdvanceReservationQuotas,
  mhtcetReservationQuotas,
  wbjeeMainsAdvanceReservationQuotas
} from '../../constants/CollegePredictorConstants';

export const getCutOffRangeFromExpectedRank = (
  expectedRank: number
): number[] => {
  const maxRank = 500000;
  if (expectedRank <= 50) {
    return [Math.max(1, expectedRank - 3), maxRank];
  } else if (expectedRank <= 100) {
    return [expectedRank - 10, maxRank];
  } else if (expectedRank <= 1000) {
    return [expectedRank - 30, maxRank];
  } else if (expectedRank <= 5000) {
    return [expectedRank - 50, maxRank];
  } else if (expectedRank <= 10000) {
    return [expectedRank - 100, maxRank];
  } else if (expectedRank <= 16000) {
    return [expectedRank - 500, maxRank];
  } else if (expectedRank <= 30000) {
    return [expectedRank - 800, maxRank];
  } else if (expectedRank <= 50000) {
    return [expectedRank - 1000, maxRank];
  } else if (expectedRank <= 100000) {
    return [expectedRank - 1200, maxRank];
  } else {
    return [expectedRank - 2000, maxRank];
  }
};

export const getCutOffRangeFromExpectedPercentile = (
  expectedPercentile: number
): number[] => {
  if (expectedPercentile < 50) {
    return [0, expectedPercentile + 2.5];
  } else if (expectedPercentile < 70) {
    return [0, expectedPercentile + 2];
  }
  if (expectedPercentile < 80) {
    return [0, expectedPercentile + 1];
  }
  if (expectedPercentile < 90) {
    return [0, expectedPercentile + 0.8];
  }
  return [0, Math.min(100, expectedPercentile + 0.5)];
};

export const getReservationCategoryCodes = (
  filteredExamNameEnum: string,
  selectGender: string,
  reservationCategoryCode: string,
  selectedQuota?: string
): string[] => {
  let reservationCategoryCodes: string[] = [];
  if (filteredExamNameEnum === 'JEE_MAINS_2025') {
    const jeeMainsQuotas = ['AI', 'GO', 'HS', 'JK', 'LA', 'OS'];
    for (const quota of jeeMainsQuotas) {
      const categoryQuota =
        selectGender === 'Female'
          ? `${reservationCategoryCode}_GIRLS_${quota}`
          : `${reservationCategoryCode}_${quota}`;

      if (jeeMainsAdvanceReservationQuotas.includes(categoryQuota)) {
        reservationCategoryCodes.push(categoryQuota);
      }
    }
  } else if (filteredExamNameEnum === 'JEE_ADVANCE_2025') {
    const aiQuotaCategoryCode =
      selectGender === 'Female'
        ? `${reservationCategoryCode}_GIRLS_AI`
        : `${reservationCategoryCode}_AI`;
    reservationCategoryCodes = [aiQuotaCategoryCode];
  } else if (filteredExamNameEnum === 'WBJEE_2025') {
    const quotaList =['AI', 'HS'];
    for (const quota of quotaList) {
      const categoryQuota = `${reservationCategoryCode}_${quota}`;
      if (wbjeeMainsAdvanceReservationQuotas.includes(categoryQuota)) {
        reservationCategoryCodes.push(categoryQuota);
      }
    }
  } else if (filteredExamNameEnum === 'WBJEE_MAINS_2025' || filteredExamNameEnum === 'WBJEE_JEE_MAINS_2025') {
    reservationCategoryCodes.push('OPEN_AI');
  } else if (filteredExamNameEnum === 'MHTCET_2025') {
    const mhtcetMainsQuotas = ['I', 'MH', 'VII', 'II', 'I-Non PWD', 'I-Non Defence'];
    for (const quota of mhtcetMainsQuotas) {
      const categoryQuota = `${reservationCategoryCode}_${quota}`;
      if (mhtcetReservationQuotas.includes(categoryQuota)) {
        reservationCategoryCodes.push(categoryQuota);
      }
    }
  }

  return reservationCategoryCodes.length > 0
    ? reservationCategoryCodes
    : [reservationCategoryCode];
};

export type CollegePredictorRequestObject = GetCollegePredictorRequestType & {
  limit?: number;
};

export interface BuildCollegePredictorRequestParams {
  values: Record<string, any>;
  collegePredictorTabsInfo?: Record<string, any>;
  selectedTab?: string;
  formId?: string;
  limit: number;
}

export interface CollegePredictorRequestOverrides {
  branchCodes?: string[];
  districts?: string[];
  limit?: number;
}

export const buildCollegePredictorRequestObject = (
  {
    values,
    collegePredictorTabsInfo,
    selectedTab,
    formId,
    limit
  }: BuildCollegePredictorRequestParams,
  overrides: CollegePredictorRequestOverrides = {}
): CollegePredictorRequestObject => {
  const {
    expectedRank,
    entranceExamNameEnum,
    admissionCategoryNameEnum,
    reservationCategoryCode,
    preferredDistricts,
    preferredBranches,
    expectedPercentile,
    sortOrderEnum,
    selectGender,
    quota
  } = values;

  const tabsInfo = collegePredictorTabsInfo ?? {};
  const activeTab = selectedTab ?? '';

  const filteredExamNameEnum =
    formId === 'mht-cet-college-predictor'
      ? entranceExamNameEnum
      : tabsInfo[activeTab]?.['examNameEnum'] ?? entranceExamNameEnum;

  let filteredExpectedRank =
    tabsInfo[activeTab]?.['expectedRank'] ?? expectedRank;

  if (filteredExamNameEnum === 'WBJEE_2025') {
    filteredExpectedRank = values['wbjeeGeneralRank'];
  } else if (filteredExamNameEnum === 'WBJEE_MAINS_2025') {
    filteredExpectedRank = values['jeeMainGeneralRank'];
  } else if (filteredExamNameEnum === 'JEE_MAINS_2025') {
    filteredExpectedRank = values['jeeMainGeneralRank'];
  } else if (filteredExamNameEnum === 'JEE_ADVANCE_2025') {
    filteredExpectedRank = values['jeeAdvanceGeneralRank'];
  }

  const filteredAdmissionCategoryNameEnum =
    tabsInfo[activeTab]?.['admissionCategoryNameEnum'] ??
    admissionCategoryNameEnum;

  const cutoffRange = expectedPercentile
    ? getCutOffRangeFromExpectedPercentile(Number(expectedPercentile))
    : getCutOffRangeFromExpectedRank(Number(filteredExpectedRank));

  const requestObject: CollegePredictorRequestObject = {
    entrance_exam_name_enum: filteredExamNameEnum,
    admission_category_name_enum: filteredAdmissionCategoryNameEnum,
    cutoff_from: cutoffRange[0],
    cutoff_to: cutoffRange[1],
    reservation_category_codes: getReservationCategoryCodes(
      filteredExamNameEnum,
      selectGender,
      reservationCategoryCode,
      quota
    ),
    branch_codes: overrides.branchCodes ?? preferredBranches ?? [],
    limit: overrides.limit ?? limit,
    districts: overrides.districts ?? preferredDistricts ?? [],
    sort_order: sortOrderEnum
  };

  return requestObject;
};
